import React, { useState } from 'react';
import {
    Box,
    Container,
    TextField,
    Button,
    Typography,
    MenuItem,
    Grid,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';

const commonInputProps = {
    style: { color: 'white' },
    sx: {
        borderRadius: '5px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
            borderWidth: '2px',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
            borderWidth: '2px',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
            borderWidth: '2px',
        },
        '& .MuiSelect-icon': {
            color: 'white',
        },
        input: { color: 'white' },
        '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #1f1f1f inset',
            WebkitTextFillColor: 'white',
        },
    },
};

const commonInputLabelProps = {
    style: { color: '#fff' },
    sx: {
        '&.Mui-focused': { color: 'white' },
    },
};

const hubOptions = {
    'Nigeria': [
        { label: 'Lagos Hub', value: 'lagos' },
        { label: 'Abuja Hub', value: 'abuja' },
        { label: 'Port Harcourt Hub', value: 'portharcourt' },
        { label: 'Enugu Hub', value: 'enugu' },
        { label: 'Ibadan Hub', value: 'ibadan' },
    ],
    'South Africa': [
        { label: 'Gauteng Hub', value: 'gauteng' },
        { label: 'Western Cape Hub', value: 'westerncape' },
        { label: 'KwaZulu-Natal Hub', value: 'kwazulunatal' },
        // { label: 'Eastern Cape Hub', value: 'easterncape' },
        { label: 'Other Provinces (FS, LP, MP, NW, NC)', value: 'southafrica' },
    ],
    // 'United States': [
    //     { label: 'Dallas Hub', value: 'unitedstates' },
    // ],
    'United Kingdom': [
        { label: 'London Hub', value: 'unitedkingdom' },
    ],
    // 'Canada': [
    //     { label: 'Ontario Hub', value: 'canada' },
    // ],
    'Ghana': [
        { label: 'Accra Hub', value: 'ghana' },
    ],
    'Kenya': [
        { label: 'Nairobi Hub', value: 'kenya' },
    ],
    // 'Zambia': [
    //     { label: 'Lusaka Hub', value: 'zambia' },
    // ],
    // 'India': [
    //     { label: 'New Delhi and Mumbai', value: 'india' },
    // ],
    'General': [
        { label: 'Global Hub (all countries)', value: 'general' },
    ],
};


function JoinCircleForm() {
    const [formData, setFormData] = useState({
        email: '',
        country: '',
        hub: '',
    });

    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
            ...(name === 'country' ? { hub: '' } : {}),
        }));
    };

    const validateForm = () =>
        formData.email.trim() && formData.country.trim() && formData.hub.trim();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            setErrorMessage('Please fill out all fields.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch('/api/circle/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email, hub: formData.hub }),
            });

            const data = await res.json();
            if (data.success) {
                setOpenDialog(true);
                setFormData({ email: '', country: '', hub: '' });
                setErrorMessage('');
            } else {
                setErrorMessage(data.error || 'Submission failed.');
            }
        } catch (err) {
            setErrorMessage('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ backgroundColor: '#1f1f1f', py: 4 }}>
            <Container maxWidth="sm">
                <Box component="form" onSubmit={handleSubmit}>
                    <Alert severity="warning" sx={{ mb: 4 }}>
                        Please note that this action can only be performed once.
                        Each individual is permitted to belong to only one CSN Circle.
                    </Alert>
                    {errorMessage && (
                        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ mb: 3 }}>
                            {errorMessage}
                        </Alert>
                    )}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                InputLabelProps={commonInputLabelProps}
                                InputProps={commonInputProps}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                required
                                InputLabelProps={commonInputLabelProps}
                                InputProps={commonInputProps}
                            >
                                {Object.keys(hubOptions).map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {formData.country && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Closest CSN Circle"
                                    name="hub"
                                    value={formData.hub}
                                    onChange={handleChange}
                                    required
                                    InputLabelProps={commonInputLabelProps}
                                    InputProps={commonInputProps}
                                >
                                    {hubOptions[formData.country]?.map((hub) => (
                                        <MenuItem key={hub.value} value={hub.value}>
                                            {hub.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        <Grid item xs={12} textAlign="center">
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loading}
                                sx={{
                                    mt: 2,
                                    minWidth: 180,
                                    backgroundColor: '#2F96FF',
                                    '&:hover': { backgroundColor: '#007ae1' },
                                    '&:active': { backgroundColor: '#2F96FF' },
                                }}
                            >
                                {loading ? 'Submitting...' : 'Join Circle'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Invitation Sent</DialogTitle>
                <DialogContent>
                    <Typography>
                        You’ve been successfully invited to join a CSN Circle.
                        Please check your email for next steps and additional information.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default JoinCircleForm;
